import { TrackingEvent } from 'library/Types';
import moment from 'moment';
import { DASHBOARD_STATUS_GROUPS, TIMELINE_STATUS_GROUPS, TIMELINE_STATUS_ORDER } from './constants';
import Helper from 'library/Helper';

export const getDashboardStatusGroup = (rawStatus: string): string => {
    const groups = DASHBOARD_STATUS_GROUPS as Record<string, string[]>;
    const matchedGroup = Object.entries(groups).find(([groupName, statuses]) => {
        if (groupName === 'All') return false;
        return statuses.includes(rawStatus);
    });
    return matchedGroup ? matchedGroup[0] : 'In Transit'; // by default show In Transit if no match
};

export const getTimelineStatus = (rawStatus: string): string | null => {
    const groups = TIMELINE_STATUS_GROUPS as Record<string, string[]>;
    const matchedGroup = Object.entries(groups).find(([, statuses]) => statuses.includes(rawStatus));
    return matchedGroup ? matchedGroup[0] : null;
};


export interface TimelineEvent {
    status: string;
    events: TrackingEvent[];
    latestEvent: TrackingEvent | null;
    isAchieved: boolean;
}

export const groupTrackingEventsForTimeline = (
    tracking: TrackingEvent[],
): TimelineEvent[] => {
    const groupedMap: Record<string, TrackingEvent[]> = {};

    const trackingInDisplayOrder = tracking.slice().reverse();

    trackingInDisplayOrder.forEach((event) => {
        const timelineStatus = getTimelineStatus(event.type);
        if (timelineStatus) {
            if (!groupedMap[timelineStatus]) {
                groupedMap[timelineStatus] = [];
            }
            groupedMap[timelineStatus].push(event);
        }
    });

    const chronologicalStatuses: string[] = [];
    trackingInDisplayOrder.forEach((event) => {
        const timelineStatus = getTimelineStatus(event.type);
        if (timelineStatus && !chronologicalStatuses.includes(timelineStatus)) {
            chronologicalStatuses.push(timelineStatus);
        }
    });

    // Remove 'Cancelled' from default order - it should only appear if events exist
    const finalOrder = TIMELINE_STATUS_ORDER.filter(
        (status) => status.toLowerCase() !== 'cancelled',
    );

    const cancelledLabel = TIMELINE_STATUS_ORDER.find((status) => status.toLowerCase() === 'cancelled') || 'Cancelled';
    const cancellationIndex = chronologicalStatuses.findIndex((status) => status.toLowerCase() === 'cancelled');

    // Only add 'Cancelled' if there are actual cancelled events
    if (cancellationIndex !== -1) {
        // Find the position to insert 'Cancelled' based on when it occurred chronologically
        let insertAfterIndex = -1;
        for (let i = cancellationIndex - 1; i >= 0; i -= 1) {
            const previousStatus = chronologicalStatuses[i];
            const baseIndex = finalOrder.indexOf(previousStatus);
            if (baseIndex !== -1) {
                insertAfterIndex = baseIndex;
                break;
            }
        }

        const insertionPoint = insertAfterIndex === -1 ? 0 : insertAfterIndex + 1;
        finalOrder.splice(insertionPoint, 0, cancelledLabel);
    }

    const result: TimelineEvent[] = finalOrder.map((status) => {
        const events = groupedMap[status] || [];
        // Since tracking is reversed (newest first), get the first element (index 0) for latestEvent
        const latestEvent = events.length > 0 ? events[0] : null;

        return {
            status,
            events,
            latestEvent,
            isAchieved: events.length > 0,
        };
    });

    return result;
};

export const getEventName = (event: TrackingEvent, t: (key: string) => string, i18n): string => {
    if (event.strAction) {
        return event.strAction;
    }
    if (event.event_name) {
        return event.event_name;
    }
    if (i18n.exists(event.type)) {
        return t(event.type);
    }
    return Helper.snakeToPretty(event.type);
};

export const getDate = (input: string): string | null => {
    return input ? moment(input).format('DD MMM YYYY') : null;
};

export const getTime = (input: string): string | null => {
    return input ? moment(input).format('hh:mm A') : null;
};
